// Small live badge counts for the sidebar — one realtime subscription per
// queue, refetching just that queue's count (a cheap `getList(1, 1, ...)`,
// which returns `totalItems` without pulling every row) on any change.
// PocketBase's `subscribe("*", ...)` doesn't itself filter server-side by a
// query the way Firestore's `onSnapshot(query(...))` did — it fires for
// every create/update/delete on the collection, so the refetch (not the
// event payload) is what applies the actual filter.
import { useEffect, useState } from "react";
import { pb } from "./pocketbase";
import { canAccess, useAuth } from "./auth";
import type { ModuleKey } from "./types";

const QUEUES: { key: ModuleKey; collection: string; filter: string }[] = [
  { key: "reports", collection: "reports", filter: "status = 'open'" },
  { key: "tickets", collection: "supportTickets", filter: "status = 'open'" },
  { key: "listingApprovals", collection: "listings", filter: "category = 'commercial' && status = 'pending_review'" },
  { key: "userVerification", collection: "verifications", filter: "status = 'pending'" },
];

export function useQueueCounts(): Partial<Record<ModuleKey, number>> {
  const { admin } = useAuth();
  const role = admin?.role ?? null;
  const [counts, setCounts] = useState<Partial<Record<ModuleKey, number>>>({});

  useEffect(() => {
    let cancelled = false;
    const unsubs: Array<() => void> = [];

    async function refetch(q: (typeof QUEUES)[number]) {
      try {
        const page = await pb.collection(q.collection).getList(1, 1, { filter: q.filter });
        if (!cancelled) setCounts((c) => ({ ...c, [q.key]: page.totalItems }));
      } catch {
        // best-effort — a stale/missing badge count isn't worth surfacing an error for
      }
    }

    // Only the queues this role's modules cover — the server refuses the rest (pb_hooks/admin-roles.js).
    setCounts({});
    for (const q of QUEUES.filter((q) => canAccess(role, q.key))) {
      refetch(q);
      pb.collection(q.collection)
        .subscribe("*", () => refetch(q))
        .then((unsub) => {
          if (cancelled) unsub();
          else unsubs.push(unsub);
        })
        .catch(() => {
          // best-effort, same as refetch
        });
    }

    return () => {
      cancelled = true;
      unsubs.forEach((unsub) => unsub());
    };
  }, [role]);

  return counts;
}
